/* eslint-disable unused-imports/no-unused-imports */
import React, {FC, useCallback, useEffect} from "react"
import {type ModalProps} from "@mui/material"
import {breakpoints} from "@next/themes"
import {TextModel} from "models/Text"
import connect from "./connect"
import {
    ShowFiltersContainer,
    DrawerContainer,
    FiltersContainerHeader,
    FiltersContainerFooter,
    FiltersBodyContainer,
    CenteredFlexContainer,
    CloseIcon,
    CloseIconButton,
    DrawerContent,
    FiltersContainer,
    EmptyFlexContainer,
    TabbedFacetsContainer,
} from "./components"
import TabbedFacet from "./tabbedFacet"
import SortTabbedFacet from "./sortTabbedFacet"
import FiltersMenuButton from "./filtersMenuButton"
import FiltersMobileTitle from "./filtersMobileTitle"
import ShowFiltersCloseButton from "./showFiltersCloseButton"
import TabbedFeatFacet from "./tabbedFeatFacet"
import TabbedFiltersPanel from "./tabbedFiltersPanel"
import TabbedFilterViewResults from "./tabbedFilterViewResults"
import TrackMobileFiltersOpen from "../../events/trackEvent/events/trackMobileFiltersOpen"
import TrackMobileFiltersClosed from "../../events/trackEvent/events/trackMobileFiltersClosed"
import {FilterFacet, Filters} from "../../models/Filter"
import {isPriceFilter} from "../../models/Filter/typeGuards"
import {useMediaQuery} from "../../hooks/useMediaQuery"

import env from "../../config/env"
import {SORT} from "../../config/constants"

interface Prop {
    filters: Filters
    isAnyFilterSelected: boolean
    clearAll: (enableSortBy: boolean) => void
    syncTabbedFilters: (isOpen: boolean) => void
    closeTabbedFilters: (isOpen: boolean) => void
    isOpen: boolean
    hasSelectedFacet: boolean
    text: TextModel
    singleOptionFacetList: string[]
    disableIcon: boolean
    enableSortBy?: boolean
    firstFilterList?: string[]
    newFilterSort: string[]
}

const CLOSE_WHITE = "next_close-white.svg"
const CLOSE_BLACK = "next_close-black.svg"

export const ShowFilters: FC<Prop> = ({
    filters,
    isAnyFilterSelected,
    clearAll,
    syncTabbedFilters,
    closeTabbedFilters,
    isOpen,
    hasSelectedFacet,
    text,
    singleOptionFacetList,
    disableIcon,
    enableSortBy = false,
    firstFilterList = [],
    newFilterSort,
}) => {
    const isInPageFiltersBreakpoint = useMediaQuery(`(min-width:${breakpoints.values.lg}px)`)
    const isMdScreenBreakpoint = useMediaQuery(`(min-width:${breakpoints.values.md}px)`)

    const openFiltersPanel = useCallback(() => {
        TrackMobileFiltersOpen()
        syncTabbedFilters(true)
    }, [syncTabbedFilters])

    const closeFiltersPanel = useCallback(() => {
        TrackMobileFiltersClosed()
        closeTabbedFilters(false)
    }, [closeTabbedFilters])

    useEffect(() => {
        if (isOpen && isInPageFiltersBreakpoint) {
            closeFiltersPanel()
        }
    }, [closeFiltersPanel, isOpen, isInPageFiltersBreakpoint])

    const modalProps: Partial<ModalProps> = {
        keepMounted: false,
        hideBackdrop: !isMdScreenBreakpoint,
    }

    const icon = isMdScreenBreakpoint ? CLOSE_WHITE : CLOSE_BLACK

    return (
        <ShowFiltersContainer>
            {!isInPageFiltersBreakpoint && (
                <FiltersMenuButton
                    openFiltersPanel={openFiltersPanel}
                    disableIcon={disableIcon}
                    firstFilterList={firstFilterList}
                />
            )}

            <DrawerContainer
                className="platform_modernisation_plp"
                anchor="right"
                BackdropProps={{className: "plp-filters-drawer-back-drop"}}
                open={isOpen}
                onClose={closeFiltersPanel}
                ModalProps={modalProps}
            >
                <DrawerContent data-testid="plp-filters-drawer-content" isFullWidthMode={!isMdScreenBreakpoint}>
                    <FiltersContainerHeader isFullWidthMode={!isMdScreenBreakpoint}>
                        <CenteredFlexContainer>
                            <CloseIconButton
                                data-testid="plp-filters-close-button"
                                isFullWidthMode={!isMdScreenBreakpoint}
                                onClick={closeFiltersPanel}
                            >
                                <CloseIcon
                                    isFullWidthMode={!isMdScreenBreakpoint}
                                    src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/${icon}`}
                                    alt={text.assetsAlt.closeIcon}
                                />
                            </CloseIconButton>
                            <FiltersMobileTitle text={text} />
                        </CenteredFlexContainer>
                        <ShowFiltersCloseButton
                            isAnyFilterSelected={isAnyFilterSelected}
                            closeFiltersPanel={closeFiltersPanel}
                            clearAll={() => clearAll(enableSortBy)}
                            text={text}
                        />
                    </FiltersContainerHeader>
                    <FiltersBodyContainer data-testid="plp-filters-body-container">
                        <TabbedFacetsContainer hasSelectedFacet={hasSelectedFacet}>
                            <FiltersContainer>
                                {newFilterSort.map((name, index) => {
                                    if (filters[name] || (enableSortBy && name === SORT)) {
                                        const {
                                            displayName,
                                            facets,
                                            type,
                                            name: filterName,
                                        } = (filters[name] || {}) as FilterFacet

                                        if (type === "feat") {
                                            return <TabbedFeatFacet key={name} filters={facets} />
                                        }

                                        if (name === SORT) {
                                            return <SortTabbedFacet key={index} />
                                        }
                                        if (
                                            isPriceFilter(filters[name]) ||
                                            (type === "filter" && facets.length > 1) ||
                                            singleOptionFacetList.includes(filterName)
                                        ) {
                                            return (
                                                <TabbedFacet
                                                    key={name}
                                                    facetName={name}
                                                    facetDisplayName={displayName}
                                                    isFirstFacet={enableSortBy ? false : index === 1}
                                                />
                                            )
                                        }
                                    }
                                    return <React.Fragment key={name} />
                                })}
                            </FiltersContainer>
                            {hasSelectedFacet && <EmptyFlexContainer />}
                        </TabbedFacetsContainer>
                        <TabbedFiltersPanel />
                    </FiltersBodyContainer>
                    <FiltersContainerFooter isFullWidthMode={!isMdScreenBreakpoint}>
                        <TabbedFilterViewResults />
                    </FiltersContainerFooter>
                </DrawerContent>
            </DrawerContainer>
        </ShowFiltersContainer>
    )
}

export default connect(ShowFilters)
